import {
  loadSources,
  sources,
  prepareForSources,
  hashComponents,
  load,
} from "@zhyswan/fingerprintjs";

// 不从 ./utils 等模块引入，保持本模块无依赖，避免和 api.ts 形成循环依赖

let fullFingerprint: Promise<string> | undefined;
let simpleFingerprint: Promise<string> | undefined;

export function getFullFingerprint() {
  if (!fullFingerprint) {
    fullFingerprint = load({
      monitoring: false,
    })
      .then((fp) => fp.get())
      .then((result) => result.visitorId)
      .catch((err) => {
        fullFingerprint = undefined;
        throw err;
      });
  }
  return fullFingerprint;
}

const EXCLUDE_LIST = [
  "fontPreferences",
  "screenFrame",
  "screenResolution",
  "touchSupport",
];

export function getSimpleFingerprint() {
  if (!simpleFingerprint) {
    simpleFingerprint = (async () => {
      const debug = false;

      await prepareForSources();
      const getComponents = loadSources(
        sources,
        { cache: {}, debug },
        EXCLUDE_LIST,
      );
      const components = await getComponents();
      return hashComponents(components);
    })().catch((err) => {
      simpleFingerprint = undefined;
      throw err;
    });
  }
  return simpleFingerprint;
}

// 指纹缓存在 localStorage，跨页面、跨标签页共享
const STORAGE_KEY = "FingerprintCache";
const STORAGE_TTL = 7 * 24 * 60 * 60 * 1000; // 7天

type FingerprintCache = { full: string; simple: string; expiresAt: number };

function readStorage(): FingerprintCache | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const cache = JSON.parse(raw) as FingerprintCache;
    if (cache?.full && cache?.simple && Date.now() < cache.expiresAt) {
      return cache;
    }
  } catch (error) {
    console.error("读取指纹缓存失败:", error);
  }
}

function writeStorage(full: string, simple: string) {
  try {
    const cache: FingerprintCache = {
      full,
      simple,
      expiresAt: Date.now() + STORAGE_TTL,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error("写入指纹缓存失败:", error);
  }
}

// sidebar 以第三方 iframe 嵌在宿主页面里，存储/Cookie 等特征受限，自己算的指纹可能和插件不一致，
// 因此在 iframe 中向插件 content script 索取指纹
function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function requestParentFingerprint(
  timeout = 5000,
): Promise<{ full: string; simple: string } | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve(undefined);
    }, timeout);

    function onMessage(e: MessageEvent) {
      if (e.source !== window.parent || e.data?.type !== "sidebar:fingerprint") {
        return;
      }
      cleanup();
      const { full, simple } = e.data;
      resolve(full && simple ? { full, simple } : undefined);
    }

    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
    }

    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type: "sidebar:getFingerprint" }, "*");
  });
}

async function computeFingerprints() {
  const [full, simple] = await Promise.all([
    getFullFingerprint(),
    getSimpleFingerprint(),
  ]);
  return { full, simple };
}

let fingerprints:
  | Promise<{ full: string; simple: string } | undefined>
  | undefined;

function getFingerprints() {
  if (!fingerprints) {
    fingerprints = (async () => {
      if (isInIframe()) {
        const parentFp = await requestParentFingerprint();
        if (parentFp) return parentFp;
        // 插件未响应时退回本地计算，不写缓存，避免把不一致的指纹存下来
        return computeFingerprints();
      }
      const cache = readStorage();
      if (cache) return cache;
      const fp = await computeFingerprints();
      writeStorage(fp.full, fp.simple);
      return fp;
    })().catch((err) => {
      console.error("获取指纹失败:", err);
      // 失败后允许下次重试
      fingerprints = undefined;
      return undefined;
    });
  }
  return fingerprints;
}

/**
 * 预热指纹：在入口处尽早调用，让首个接口请求发出时指纹已就绪
 */
export function prewarmFingerprint() {
  getFingerprints();
}

/**
 * 获取指纹请求头，最多等待 timeout 毫秒，超时或失败返回空对象，不阻塞请求
 * 有缓存时几毫秒即可返回，只有首次计算时才可能等满 timeout
 */
export async function getFingerprintHeaders(
  timeout = 3000,
): Promise<Record<string, string>> {
  const result = await Promise.race([
    getFingerprints(),
    new Promise<undefined>((resolve) => setTimeout(resolve, timeout)),
  ]);
  if (!result) return {};
  return {
    "X-FP-Full": result.full,
    "X-FP-Simple": result.simple,
  };
}
